import { makeAutoObservable, runInAction } from "mobx";
import FirebaseService from "./FirebaseService";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  arrayUnion,
  increment,
  Unsubscribe,
  Timestamp,
} from "firebase/firestore";
import { Plan } from "./PlansService";

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL || "https://us-central1-trail-tools.cloudfunctions.net";

export type ReviewInvite = {
  id: string;
  planId: string;
  planOwnerId: string;
  reviewerEmail: string;
  reviewerName?: string;
  reviewerUserId?: string;
  status: "pending" | "accepted";
  invitedAt: Date;
  acceptedAt?: Date;
};

export type ReviewMessage = {
  authorId: string;
  text: string;
  createdAt: Date;
  readBy: string[];
};

export type ReviewThread = {
  id: string;
  planId: string;
  planOwnerId: string;
  reviewerUserId: string;
  sectionIndex: number;
  messages: ReviewMessage[];
  lastMessageAt: Date | null;
  unreadCount: Record<string, number>;
};

export default class ReviewService {
  private _firebaseService: FirebaseService;
  private _threads: ReviewThread[] = [];
  private _invites: ReviewInvite[] = [];
  private _sharedPlans: Plan[] = [];
  private _ownerNames: Record<string, string> = {};
  private _isLoading: boolean = false;
  private _isSendingInvite: boolean = false;
  private _error: string | null = null;
  private _threadUnsubscribes: Unsubscribe[] = [];

  constructor(firebaseService: FirebaseService) {
    this._firebaseService = firebaseService;
    makeAutoObservable(this, undefined, { autoBind: true, deep: false });
  }

  get threads(): ReviewThread[] { return this._threads; }
  get invites(): ReviewInvite[] { return this._invites; }
  get sharedPlans(): Plan[] { return this._sharedPlans; }
  get ownerNames(): Record<string, string> { return this._ownerNames; }
  get isLoading(): boolean { return this._isLoading; }
  get isSendingInvite(): boolean { return this._isSendingInvite; }
  get error(): string | null { return this._error; }

  private _threadId(planOwnerId: string, planId: string, reviewerUserId: string, sectionIndex: number): string {
    return `${planOwnerId}-${planId}-${reviewerUserId}-${sectionIndex}`;
  }

  private _parseThread(id: string, data: any): ReviewThread {
    return {
      id,
      planId: data.planId,
      planOwnerId: data.planOwnerId,
      reviewerUserId: data.reviewerUserId,
      sectionIndex: data.sectionIndex,
      messages: (data.messages || []).map((m: any) => ({
        authorId: m.authorId,
        text: m.text,
        createdAt: m.createdAt instanceof Timestamp ? m.createdAt.toDate() : new Date(m.createdAt),
        readBy: m.readBy || [],
      })),
      lastMessageAt: data.lastMessageAt instanceof Timestamp ? data.lastMessageAt.toDate() : null,
      unreadCount: data.unreadCount || {},
    };
  }

  private _parseInvite(id: string, data: any): ReviewInvite {
    return {
      id,
      planId: data.planId,
      planOwnerId: data.planOwnerId,
      reviewerEmail: data.reviewerEmail,
      reviewerUserId: data.reviewerUserId,
      status: data.status,
      invitedAt: data.invitedAt instanceof Timestamp ? data.invitedAt.toDate() : new Date(),
      acceptedAt: data.acceptedAt instanceof Timestamp ? data.acceptedAt.toDate() : undefined,
    };
  }

  async sendInvite(planId: string, planOwnerId: string, reviewerEmail: string): Promise<void> {
    this._isSendingInvite = true;
    this._error = null;
    try {
      const currentUser = this._firebaseService.auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");
      const token = await currentUser.getIdToken(true);

      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/sendReviewInvite`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ planId, planOwnerId, reviewerEmail }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      // Reload invites
      await this.loadInvitesForPlan(planId, planOwnerId);
    } catch (error) {
      runInAction(() => { this._error = (error as Error).message; });
      throw error;
    } finally {
      runInAction(() => { this._isSendingInvite = false; });
    }
  }

  async loadInvitesForPlan(planId: string, planOwnerId: string): Promise<void> {
    try {
      const db = this._firebaseService.db;
      const q = query(
        collection(db, "reviewInvites"),
        where("planId", "==", planId),
        where("planOwnerId", "==", planOwnerId)
      );
      const snapshot = await getDocs(q);
      const invites = snapshot.docs.map(d => this._parseInvite(d.id, d.data()));

      // Enrich with displayName for accepted invites that have a userId
      const enriched = await Promise.all(
        invites.map(async invite => {
          if (!invite.reviewerUserId) return invite;
          try {
            const userSnap = await getDoc(doc(db, "users", invite.reviewerUserId));
            if (userSnap.exists()) {
              return { ...invite, reviewerName: userSnap.data().displayName as string | undefined };
            }
          } catch (_e) {}
          return invite;
        })
      );

      runInAction(() => { this._invites = enriched; });
    } catch (error) {
      console.error("loadInvitesForPlan error:", error);
    }
  }

  async loadSharedPlans(reviewerUserId: string): Promise<void> {
    this._isLoading = true;
    try {
      const db = this._firebaseService.db;
      const q = query(
        collection(db, "reviewInvites"),
        where("reviewerUserId", "==", reviewerUserId),
        where("status", "==", "accepted")
      );
      const snapshot = await getDocs(q);

      const plans: Plan[] = [];
      const ownerNames: Record<string, string> = {};
      for (const inviteDoc of snapshot.docs) {
        const invite = inviteDoc.data();

        // Only show plans from owners with an active subscription
        const ownerSnap = await getDoc(doc(db, "users", invite.planOwnerId));
        const ownerData = ownerSnap.data();
        if (!this._ownerHasActiveSub(ownerData)) continue;
        if (ownerData?.displayName) {
          ownerNames[invite.planOwnerId] = ownerData.displayName;
        }

        const planDocId = `${invite.planOwnerId}-${invite.planId}`;
        const planRef = doc(db, "plans", planDocId);
        const planSnap = await getDoc(planRef);
        if (planSnap.exists()) {
          const data = planSnap.data();
          plans.push({
            id: invite.planId,
            name: data.name,
            date: data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date),
            gpxFile: data.gpxFile,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
            modifiedAt: data.modifiedAt instanceof Timestamp ? data.modifiedAt.toDate() : new Date(),
            createdBy: data.createdBy || invite.planOwnerId,
            distance: data.distance,
            elevationGain: data.elevationGain,
            elevationLoss: data.elevationLoss,
            sections: data.sections || [],
            controlPoints: data.controlPoints || [],
            plannedEffort: data.plannedEffort || [],
            plannedNutrition: data.plannedNutrition || [],
            notes: data.notes || [],
            type: data.type || "race",
            sport: data.sport,
            userId: invite.planOwnerId,
            raceId: data.raceId,
            svg: data.svg,
            surfaceData: data.surfaceData,
            comparisonId: data.comparisonId,
          } as Plan);
        }
      }

      runInAction(() => {
        this._sharedPlans = plans;
        this._ownerNames = ownerNames;
        this._isLoading = false;
      });
    } catch (error) {
      console.error("loadSharedPlans error:", error);
      runInAction(() => { this._isLoading = false; });
    }
  }

  subscribeToThreadsForPlan(planId: string, planOwnerId: string, currentUserId: string): void {
    this._threadUnsubscribes.forEach(u => u());
    this._threadUnsubscribes = [];

    const db = this._firebaseService.db;

    const mergeSnapshot = (incoming: ReviewThread[]) => {
      runInAction(() => {
        const otherThreads = this._threads.filter(
          t => !incoming.some(u => u.id === t.id)
        );
        this._threads = [...otherThreads, ...incoming];
      });
    };

    // Threads where current user is the plan owner
    const q1 = query(
      collection(db, "reviewThreads"),
      where("planId", "==", planId),
      where("planOwnerId", "==", planOwnerId)
    );
    this._threadUnsubscribes.push(
      onSnapshot(q1, snapshot => mergeSnapshot(snapshot.docs.map(d => this._parseThread(d.id, d.data()))))
    );

    // Threads where current user is the reviewer
    const q2 = query(
      collection(db, "reviewThreads"),
      where("planId", "==", planId),
      where("reviewerUserId", "==", currentUserId)
    );
    this._threadUnsubscribes.push(
      onSnapshot(q2, snapshot => mergeSnapshot(snapshot.docs.map(d => this._parseThread(d.id, d.data()))))
    );
  }

  unsubscribeFromThreads(): void {
    this._threadUnsubscribes.forEach(u => u());
    this._threadUnsubscribes = [];
    runInAction(() => { this._threads = []; });
  }

  async getOrCreateThread(
    planId: string,
    planOwnerId: string,
    reviewerUserId: string,
    sectionIndex: number
  ): Promise<ReviewThread> {
    const id = this._threadId(planOwnerId, planId, reviewerUserId, sectionIndex);
    const db = this._firebaseService.db;
    const ref = doc(db, "reviewThreads", id);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      const threadData = {
        planId,
        planOwnerId,
        reviewerUserId,
        sectionIndex,
        messages: [],
        lastMessageAt: null,
        unreadCount: {},
      };
      await setDoc(ref, threadData);
      return { id, ...threadData, lastMessageAt: null };
    }

    return this._parseThread(id, snap.data());
  }

  async postMessage(
    planId: string,
    planOwnerId: string,
    reviewerUserId: string,
    sectionIndex: number,
    authorId: string,
    text: string
  ): Promise<void> {
    await this.getOrCreateThread(planId, planOwnerId, reviewerUserId, sectionIndex);

    const id = this._threadId(planOwnerId, planId, reviewerUserId, sectionIndex);
    const db = this._firebaseService.db;
    const ref = doc(db, "reviewThreads", id);
    const otherUserId = authorId === planOwnerId ? reviewerUserId : planOwnerId;

    await updateDoc(ref, {
      messages: arrayUnion({ authorId, text, createdAt: Timestamp.now(), readBy: [authorId] }),
      lastMessageAt: Timestamp.now(),
      [`unreadCount.${otherUserId}`]: increment(1),
    });
  }

  async markThreadRead(
    planId: string,
    planOwnerId: string,
    reviewerUserId: string,
    sectionIndex: number,
    userId: string
  ): Promise<void> {
    const thread = this.getThreadForSection(planId, planOwnerId, reviewerUserId, sectionIndex);
    if (!thread || (thread.unreadCount[userId] || 0) === 0) return;

    const id = this._threadId(planOwnerId, planId, reviewerUserId, sectionIndex);
    const db = this._firebaseService.db;
    const ref = doc(db, "reviewThreads", id);
    await updateDoc(ref, { [`unreadCount.${userId}`]: 0 });
  }

  async checkReviewerAccess(planOwnerId: string, planId: string, currentUserId: string): Promise<boolean> {
    try {
      const db = this._firebaseService.db;

      // Check owner still has an active subscription
      const ownerSnap = await getDoc(doc(db, "users", planOwnerId));
      if (!this._ownerHasActiveSub(ownerSnap.data())) return false;

      const q = query(
        collection(db, "reviewInvites"),
        where("planId", "==", planId),
        where("planOwnerId", "==", planOwnerId),
        where("reviewerUserId", "==", currentUserId),
        where("status", "==", "accepted")
      );
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error("checkReviewerAccess error:", error);
      return false;
    }
  }

  async acceptPendingInvitesByEmail(email: string, reviewerUserId: string): Promise<{ planId: string; planOwnerId: string } | null> {
    try {
      const db = this._firebaseService.db;
      const q = query(
        collection(db, "reviewInvites"),
        where("reviewerEmail", "==", email.toLowerCase()),
        where("status", "==", "pending")
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      let first: { planId: string; planOwnerId: string } | null = null;
      for (const inviteDoc of snapshot.docs) {
        const data = inviteDoc.data();
        await updateDoc(doc(db, "reviewInvites", inviteDoc.id), {
          reviewerUserId,
          status: "accepted",
          acceptedAt: Timestamp.now(),
        });
        if (!first) first = { planId: data.planId, planOwnerId: data.planOwnerId };
      }
      return first;
    } catch (error) {
      console.error("acceptPendingInvitesByEmail error:", error);
      return null;
    }
  }

  async revokeInvite(inviteId: string): Promise<void> {
    const db = this._firebaseService.db;
    await deleteDoc(doc(db, "reviewInvites", inviteId));
    runInAction(() => {
      this._invites = this._invites.filter(i => i.id !== inviteId);
    });
  }

  getUnreadForPlan(planId: string, userId: string): number {
    return this._threads
      .filter(t => t.planId === planId)
      .reduce((sum, t) => sum + (t.unreadCount[userId] || 0), 0);
  }

  getThreadForSection(
    planId: string,
    planOwnerId: string,
    reviewerUserId: string,
    sectionIndex: number
  ): ReviewThread | undefined {
    const id = this._threadId(planOwnerId, planId, reviewerUserId, sectionIndex);
    return this._threads.find(t => t.id === id);
  }

  private _ownerHasActiveSub(userData: any): boolean {
    if (!userData) return false;
    // Explicit inactive always wins — even if proExpDate is still in the future
    if (userData.subscriptionStatus === "inactive") return false;
    if (userData.subscriptionStatus === "active") return true;
    // No subscriptionStatus set: fall back to proExpDate (legacy / manually-provisioned accounts)
    if (userData.proExpDate) {
      const exp = userData.proExpDate instanceof Timestamp
        ? userData.proExpDate.toDate()
        : new Date(userData.proExpDate);
      return exp > new Date();
    }
    return false;
  }

  async getOwnerDisplayName(userId: string): Promise<string | null> {
    try {
      const userSnap = await getDoc(doc(this._firebaseService.db, "users", userId));
      return userSnap.exists() ? (userSnap.data().displayName as string | null) : null;
    } catch (_e) {
      return null;
    }
  }

  clearError(): void {
    this._error = null;
  }
}
